import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Upload, Trash2, Copy, Check } from "lucide-react";

export default function MediaLibrary() {
  const { activeTenant } = useTenant();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileInput = useRef(null);

  const load = async () => {
    if (!activeTenant) return;
    const { data } = await api.get("/media");
    setItems(data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenant?.id]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/media/${id}`);
    load();
  };

  const copyUrl = (item) => {
    const full = `${API}${item.url}`;
    navigator.clipboard.writeText(full);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 1200);
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="media-library-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Website · {activeTenant?.name}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Media library</h1>
        </div>
        <label className="n-btn flex items-center gap-2 cursor-pointer" data-testid="media-upload-button">
          <Upload size={14} /> {busy ? "UPLOADING…" : "UPLOAD"}
          <input ref={fileInput} type="file" className="hidden" multiple onChange={upload} accept="image/*,application/pdf,video/*" />
        </label>
      </div>

      <div className="text-xs font-mono-plex text-[var(--n-text-muted)] mb-4">
        Uploads are stored inside Nariai (isolated per tenant). Max 8 MB per file. Paste the URL into block editors to use images.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="media-grid">
        {items.map((it) => {
          const isImage = (it.content_type || "").startsWith("image/");
          return (
            <div key={it.id} className="n-card overflow-hidden group" data-testid={`media-item-${it.id}`}>
              <div className="aspect-square bg-[#0a0a0a] flex items-center justify-center">
                {isImage ? (
                  <img src={`${API}${it.url}`} alt={it.alt || it.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-[var(--n-text-muted)] text-xs font-mono-plex uppercase">{it.content_type?.split("/")[1] || "file"}</div>
                )}
              </div>
              <div className="p-3">
                <div className="text-xs font-mono-plex truncate text-white">{it.filename}</div>
                <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] mt-1">
                  {Math.round((it.size || 0) / 1024)} KB
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => copyUrl(it)} className="n-btn-ghost n-btn text-[10px] flex items-center gap-1 flex-1 justify-center" data-testid={`media-copy-${it.id}`}>
                    {copiedId === it.id ? <><Check size={10}/>COPIED</> : <><Copy size={10}/>COPY URL</>}
                  </button>
                  <button onClick={() => del(it.id)} className="n-btn-ghost n-btn text-[10px] text-[var(--n-danger)]" data-testid={`media-delete-${it.id}`}>
                    <Trash2 size={10}/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {items.length === 0 && !busy && (
        <div className="text-center py-20 text-[var(--n-text-muted)] font-mono-plex text-sm">No media yet. Upload something above.</div>
      )}
    </div>
  );
}
